import { useEffect, useMemo, useState } from "react";
import {
  ArrowRight,
  Check,
  CheckCircle2,
  Eraser,
  RefreshCw,
  X,
} from "lucide-react";

const STEPS = [
  {
    id: "who",
    label: "WHO",
    title: "谁是句子的主角？",
    hint: "高亮表示人物或事物主体的部分。",
  },
  {
    id: "what",
    label: "WHAT",
    title: "发生了什么？",
    hint: "高亮核心动作或主要信息。",
  },
  {
    id: "when",
    label: "WHEN",
    title: "事情何时发生？",
    hint: "没有明确时间时，可以不选择任何内容。",
  },
  {
    id: "where",
    label: "WHERE",
    title: "事情在哪里发生？",
    hint: "没有明确地点时，可以不选择任何内容。",
  },
  {
    id: "why",
    label: "WHY",
    title: "为什么会发生？",
    hint: "没有明确原因时，可以不选择任何内容。",
  },
];

const WORD_SPLIT_PATTERN = /([A-Za-z]+(?:['’-][A-Za-z]+)*)/g;
const WORD_PATTERN = /^[A-Za-z]+(?:['’-][A-Za-z]+)*$/;

function SentenceDetectiveDrawer({
  sentence,
  onEvaluate,
  onSummarize,
  onClose,
}) {
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [completedSteps, setCompletedSteps] = useState(() => new Set());
  const [selectedIndexes, setSelectedIndexes] = useState(() => new Set());
  const [dragAnchor, setDragAnchor] = useState(null);
  const [result, setResult] = useState(null);
  const [isComplete, setIsComplete] = useState(false);
  const [isChecking, setIsChecking] = useState(false);
  const [summaryState, setSummaryState] = useState({
    status: "idle",
    text: "",
    error: "",
  });

  const parts = useMemo(() => {
    let wordIndex = 0;
    return sentence.split(WORD_SPLIT_PATTERN).map((text) => {
      if (!WORD_PATTERN.test(text)) return { text, wordIndex: null };

      const part = { text, wordIndex };
      wordIndex += 1;
      return part;
    });
  }, [sentence]);

  const currentStep = STEPS[currentStepIndex];
  const selectedPhrase = parts
    .filter(
      (part) => part.wordIndex !== null && selectedIndexes.has(part.wordIndex),
    )
    .map((part) => part.text)
    .join(" ");

  useEffect(() => {
    const stopDragging = () => setDragAnchor(null);
    window.addEventListener("pointerup", stopDragging);
    return () => window.removeEventListener("pointerup", stopDragging);
  }, []);

  function selectRange(start, end) {
    const lower = Math.min(start, end);
    const upper = Math.max(start, end);
    const next = new Set();
    for (let index = lower; index <= upper; index += 1) {
      next.add(index);
    }
    setSelectedIndexes(next);
    setResult(null);
  }

  function handlePointerDown(event, wordIndex) {
    if (result?.correct || isChecking) return;
    event.preventDefault();
    setDragAnchor(wordIndex);
    selectRange(wordIndex, wordIndex);
  }

  function handlePointerEnter(wordIndex) {
    if (dragAnchor === null || result?.correct || isChecking) return;
    selectRange(dragAnchor, wordIndex);
  }

  function handleKeyboardSelection(event, wordIndex) {
    if (event.detail !== 0 || result?.correct || isChecking) return;
    selectRange(wordIndex, wordIndex);
  }

  function clearSelection() {
    setSelectedIndexes(new Set());
    setResult(null);
  }

  function applyEvaluation(isCorrect, message) {
    if (isCorrect) {
      setCompletedSteps((current) => {
        const next = new Set(current);
        next.add(currentStep.id);
        return next;
      });
    }
    setResult({ correct: isCorrect, message });
  }

  async function checkAnswer() {
    setIsChecking(true);
    setResult(null);
    try {
      const evaluation = await onEvaluate(
        currentStep.id,
        sentence,
        selectedPhrase,
      );
      applyEvaluation(evaluation.isCorrect, evaluation.feedback);
    } catch (error) {
      setResult({
        correct: false,
        error: true,
        message:
          error instanceof Error
            ? error.message
            : "句子分析失败，请稍后再试。",
      });
    } finally {
      setIsChecking(false);
    }
  }

  async function loadSummary() {
    setSummaryState({ status: "loading", text: "", error: "" });
    try {
      const data = await onSummarize(sentence);
      setSummaryState({
        status: "ready",
        text: data.summary,
        error: "",
      });
    } catch (error) {
      setSummaryState({
        status: "error",
        text: "",
        error:
          error instanceof Error
            ? error.message
            : "句子总结失败，请稍后再试。",
      });
    }
  }

  function continueToNextStep() {
    if (currentStepIndex === STEPS.length - 1) {
      setIsComplete(true);
      void loadSummary();
      return;
    }

    setCurrentStepIndex((current) => current + 1);
    setSelectedIndexes(new Set());
    setResult(null);
  }

  return (
    <>
      <button
        type="button"
        className="detective-backdrop"
        aria-label="关闭句子侦探"
        onClick={onClose}
      />
      <aside
        className="sentence-detective-drawer"
        role="dialog"
        aria-modal="true"
        aria-labelledby="sentence-detective-title"
      >
        <header className="detective-header">
          <div>
            <p>SENTENCE DETECTIVE</p>
            <h2 id="sentence-detective-title">句子侦探</h2>
          </div>
          <button
            type="button"
            className="detective-close"
            aria-label="关闭句子侦探"
            title="关闭"
            onClick={onClose}
          >
            <X aria-hidden="true" size={20} strokeWidth={2} />
          </button>
        </header>

        <div className="detective-body">
          <ol className="detective-progress" aria-label="句子分析进度">
            {STEPS.map((step, index) => {
              const isCurrent = index === currentStepIndex && !isComplete;
              const isCompleted = completedSteps.has(step.id);
              const className = [
                isCurrent ? "current" : "",
                isCompleted ? "completed" : "",
              ]
                .filter(Boolean)
                .join(" ");

              return (
                <li className={className} key={step.id}>
                  <span>{isCompleted ? "✓" : index + 1}</span>
                  <strong>{step.label}</strong>
                </li>
              );
            })}
          </ol>

          {isComplete ? (
            <div className="detective-complete">
              <CheckCircle2 aria-hidden="true" size={34} strokeWidth={1.8} />
              <h3>侦探任务完成</h3>
              <p>这句话的五个线索已经检查完毕。</p>
              <section
                className="detective-summary"
                aria-live="polite"
                aria-labelledby="detective-summary-title"
              >
                <h4 id="detective-summary-title">句子分析总结</h4>
                {summaryState.status === "loading" && (
                  <p className="detective-summary-status">正在生成总结...</p>
                )}
                {summaryState.status === "ready" && (
                  <p>{summaryState.text}</p>
                )}
                {summaryState.status === "error" && (
                  <div className="detective-summary-error" role="alert">
                    <p>{summaryState.error}</p>
                    <button type="button" onClick={loadSummary}>
                      <RefreshCw
                        aria-hidden="true"
                        size={16}
                        strokeWidth={2}
                      />
                      <span>重试</span>
                    </button>
                  </div>
                )}
              </section>
              <button type="button" onClick={onClose}>
                返回文章
              </button>
            </div>
          ) : (
            <>
              <section className="detective-step-heading">
                <p>
                  STEP {currentStepIndex + 1} / {STEPS.length} ·{" "}
                  {currentStep.label}
                </p>
                <h3>{currentStep.title}</h3>
                <span>{currentStep.hint}</span>
              </section>

              <div className="detective-sentence" aria-label="选择句子中的答案">
                {parts.map((part, index) =>
                  part.wordIndex === null ? (
                    <span key={`${part.text}-${index}`}>{part.text}</span>
                  ) : (
                    <button
                      type="button"
                      className={
                        selectedIndexes.has(part.wordIndex)
                          ? "sentence-token selected"
                          : "sentence-token"
                      }
                      aria-label={`选择单词 ${part.text}`}
                      aria-pressed={selectedIndexes.has(part.wordIndex)}
                      disabled={result?.correct || isChecking}
                      key={`${part.text}-${index}`}
                      onClick={(event) =>
                        handleKeyboardSelection(event, part.wordIndex)
                      }
                      onPointerDown={(event) =>
                        handlePointerDown(event, part.wordIndex)
                      }
                      onPointerEnter={() => handlePointerEnter(part.wordIndex)}
                    >
                      {part.text}
                    </button>
                  ),
                )}
              </div>

              <div className="detective-selection-meta">
                <span>
                  {selectedIndexes.size > 0
                    ? `已高亮 ${selectedIndexes.size} 个词`
                    : "未高亮任何内容"}
                </span>
                <button
                  type="button"
                  aria-label="清空高亮"
                  title="清空高亮"
                  disabled={
                    selectedIndexes.size === 0 || result?.correct || isChecking
                  }
                  onClick={clearSelection}
                >
                  <Eraser aria-hidden="true" size={17} strokeWidth={2} />
                </button>
              </div>

              {result && (
                <div
                  className={
                    result.correct
                      ? "detective-feedback correct"
                      : result.error
                        ? "detective-feedback error"
                        : "detective-feedback incorrect"
                  }
                  role={result.error ? "alert" : "status"}
                >
                  {result.message}
                </div>
              )}

              <div className="detective-actions">
                {result?.correct ? (
                  <button
                    type="button"
                    className="detective-primary"
                    onClick={continueToNextStep}
                  >
                    <span>
                      {currentStepIndex === STEPS.length - 1 ? "完成" : "继续"}
                    </span>
                    <ArrowRight aria-hidden="true" size={18} strokeWidth={2} />
                  </button>
                ) : (
                  <button
                    type="button"
                    className="detective-primary"
                    disabled={isChecking}
                    onClick={checkAnswer}
                  >
                    <Check aria-hidden="true" size={18} strokeWidth={2} />
                    <span>{isChecking ? "检查中..." : "检查答案"}</span>
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </aside>
    </>
  );
}

export default SentenceDetectiveDrawer;
